import React, { useRef, useEffect } from "react";
import * as Sentry from "@sentry/browser";
import { useViewerQuery } from "queries/viewerQuery";
import { useEffectOnce } from "react-use";
import { isDevelopmentMode } from "tools/device";

type SuccessCallback = (message: string) => void;
type FailureCallback = (message: string) => void;
type IdCallback = (id: string) => void;
type UrlCallback = (url: string) => void;

enum ClarityLogLevel {
  None = "None",
  Error = "Error",
  Warning = "Warning",
  Info = "Info",
  Debug = "Debug",
  Verbose = "Verbose"
}

type ClarityConfig = {
  isIonic?: boolean;
  userId?: string | null;
  logLevel?: ClarityLogLevel;
  allowMeteredNetworkUsage?: boolean;
  allowedDomains?: string[];
  disableOnLowEndDevices?: boolean;
  maximumDailyNetworkUsageInMB?: number | null;
};

export interface ClarityPlugin {
  LogLevel: typeof ClarityLogLevel;
  initialize: (projectId: string, success: SuccessCallback, failure: FailureCallback, config?: ClarityConfig) => void;
  pause: (success: SuccessCallback, failure: FailureCallback) => void;
  resume: (success: SuccessCallback, failure: FailureCallback) => void;
  isPaused: (success: (isPaused: boolean) => void, failure: FailureCallback) => void;
  setCustomUserId: (userId: string, success: SuccessCallback, failure: FailureCallback) => void;
  setCustomSessionId: (sessionId: string, success: SuccessCallback, failure: FailureCallback) => void;
  getCurrentSessionId: (callback: IdCallback, failure: FailureCallback) => void;
  getCurrentSessionUrl: (callback: UrlCallback, failure: FailureCallback) => void;
  setCustomTag: (key: string, value: string, success: SuccessCallback, failure: FailureCallback) => void;
}
export interface ClarityBrowser {
  (command: "consent"): void;
  (command: "set", key: string, value: string | string[]): void;
  (command: "event", eventName: string): void;
  (command: "identify", customId: string, customSessionId?: string, customPageId?: string, friendlyName?: string): void;
  (command: "upgrade", reason: string): void;
}

const initializeCordovaMSClarity = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const success: SuccessCallback = (message: string) => {
      resolve();
    };

    const failure: FailureCallback = (message: string) => {
      const error = new Error(`MS Clarity initialization failed: ${message}`);
      reject(error);
    };

    const clarityPlugin = window.ClarityPlugin;
    if (clarityPlugin && clarityPlugin.initialize) {
      clarityPlugin.initialize("nxehmkivel", success, failure);
    } else {
      failure("ClarityPlugin not found");
    }
  });
};

const initializeWebMSClarity = (): void => {
  const script = document.createElement("script");
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "o5j6my12g4");
      
      // Improve iOS session continuity by explicitly setting consent
      window.clarity('consent');
      `;

  document.head.appendChild(script);
};

const MSClarity: React.FC = () => {
  const { viewer } = useViewerQuery();
  const userIdSetRef = useRef(false);

  // Initialize Clarity once when component mounts
  useEffectOnce(() => {
    if (isDevelopmentMode()) return;

    if (window.ClarityPlugin) {
      initializeCordovaMSClarity().catch((error) => {
        Sentry.captureException(error);
      });
    } else {
      initializeWebMSClarity();
    }
  });

  // Set user ID when viewer name becomes available
  useEffect(() => {
    if (isDevelopmentMode()) return;

    if (viewer?.name && !userIdSetRef.current) {
      if (window.ClarityPlugin) {
        window.ClarityPlugin.setCustomUserId(
          viewer.name,
          () => {},
          () => {}
        );
      } else if (window.clarity) {
        window.clarity("identify", viewer.name);
      }
      userIdSetRef.current = true;
    }
  }, [viewer?.name]);

  return null;
};

export default MSClarity;
